import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  IconButton,
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CloseIcon from '@material-ui/icons/Close';
import WarningIcon from ".././../assets/icons/warning-icon.svg";
import { useStyles } from './CourierSwitchResultDialog.styles';
import { TextConstants } from 'constants/TextConstants';
import ExpressConstants from 'constants/ExpressConstants';

const MAX_VISIBLE_CHIPS = ExpressConstants.EXPRESS_CONSTANTS.COURIER_SWITCHING.MAX_VISIBLE_SWITCHING_RESULTS_IDS;

interface CourierSwitchResultDialogProps {
  open: boolean;
  successIds: string[];
  failedIds: string[];
  onConfirm: () => void;
  onDiscard: () => void;
  onDiscardComplete?: () => void;
  selectedCourier?: string;
}

interface SectionProps {
  ids: string[];
  title: string;
  description: string;
  icon: React.ReactNode;
  showAll: boolean;
  onToggleShowAll: () => void;
  classes: ReturnType<typeof useStyles>;
  isError?: boolean;
}

const Section: React.FC<SectionProps> = React.memo(({ 
  ids, 
  title, 
  description, 
  icon, 
  showAll, 
  onToggleShowAll,
  classes,
  isError = false
}) => {
  const displayIds = useMemo(() => 
    showAll ? ids : ids.slice(0, MAX_VISIBLE_CHIPS),
    [ids, showAll]
  );
  
  const remainingCount = useMemo(() => 
    Math.max(0, ids.length - MAX_VISIBLE_CHIPS),
    [ids.length]
  );
  
  const hasMoreItems = useMemo(() => 
    ids.length > MAX_VISIBLE_CHIPS,
    [ids.length]
  );

  return (
    <Box className={classes.sectionCard}>
      <Box className={classes.sectionHeader}>
        <Box style={{ width: '100%' }}>
          <Box className={classes.sectionHeaderTop}>
            <Box className={classes.sectionTitle}>
              {icon}
              <Typography>{title}</Typography>
            </Box>
            <Typography className={classes.sectionCount}>
              {ids.length} {ids.length === 1 ? 'order' : 'orders'}
            </Typography>
          </Box>
          <Typography className={isError ? classes.descriptionError : classes.description}>
            {description}
          </Typography>
        </Box>
      </Box>
      <Box className={classes.chipsWrapper}>
        <Typography className={classes.consignmentIdsLabel}>
          {TextConstants.CONSIGNMENT_IDS}
        </Typography>
        <Box className={classes.chipsContainer}>
          {displayIds.map((id) => (
            <Chip
              key={id}
              label={id}
              size="small"
              variant="outlined"
              className={classes.chip}
            />
          ))}
          {!showAll && remainingCount > 0 && (
            <Chip
              label={`+${remainingCount} others`}
              size="small"
              variant="outlined"
              className={classes.chip}
            />
          )}
        </Box>
      </Box>
      {hasMoreItems && (
        <Button
          color="primary"
          size="small"
          onClick={onToggleShowAll}
          className={classes.toggleButton}
        >
          {showAll ? TextConstants.SHOW_LESS : TextConstants.SHOW_ALL}
        </Button>
      )}
    </Box>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.ids === nextProps.ids &&
    prevProps.showAll === nextProps.showAll &&
    prevProps.title === nextProps.title &&
    prevProps.isError === nextProps.isError
  );
});

Section.displayName = 'Section';

const CourierSwitchResultDialog: React.FC<CourierSwitchResultDialogProps> = ({
  open,
  successIds,
  failedIds,
  onConfirm,
  onDiscard,
  onDiscardComplete,
  selectedCourier
}) => {
  const classes = useStyles();
  const [showAllSuccess, setShowAllSuccess] = useState(false);
  const [showAllFailed, setShowAllFailed] = useState(false);

  useEffect(() => {
    if (!open) {
      setShowAllSuccess(false);
      setShowAllFailed(false);
    }
  }, [open]);

  const handleClose = useCallback(() => {
    onDiscard();
    onDiscardComplete?.();
  }, [onDiscard, onDiscardComplete]);

  const toggleShowAllSuccess = useCallback(() => {
    setShowAllSuccess(prev => !prev);
  }, []);

  const toggleShowAllFailed = useCallback(() => {
    setShowAllFailed(prev => !prev);
  }, []);

  const successIcon = useMemo(() => (
    <CheckCircleIcon className={classes.successIcon} />
  ), [classes.successIcon]);

  const warningIcon = useMemo(() => (
    <img src={WarningIcon} alt="warning" className={classes.icon} />
  ), [classes.icon]);

  const hasSuccessOrders = useMemo(() => successIds.length > 0, [successIds.length]);
  const hasFailedOrders = useMemo(() => failedIds.length > 0, [failedIds.length]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      classes={{ paper: classes.dialogPaper }}
      maxWidth="sm"
    >
      <DialogTitle className={classes.dialogTitle}>
        {TextConstants.CARRIER_ASSIGNMENT_SUMMARY}
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={handleClose}
        >
          <CloseIcon />
        </IconButton>
        <Typography className={classes.titleDescription}>
          {TextConstants.REVIEW_CONSIGNMENT_BREAKDOWN}
        </Typography>
       
      </DialogTitle>
      
      <DialogContent className={classes.dialogContent}>
      {selectedCourier && (
          <Typography className={classes.courierSelected}>
            {TextConstants.COURIER_SELECTED} <span >{selectedCourier}</span>
          </Typography>
        )}
        {hasSuccessOrders && (
          <Box mb={1.5}>
            <Section
              ids={successIds}
              title={TextConstants.READY_FOR_ASSIGNMENT}
              description={TextConstants.READY_FOR_ASSIGNMENT_DESC}
              icon={successIcon}
              showAll={showAllSuccess}
              onToggleShowAll={toggleShowAllSuccess}
              classes={classes}
              isError={false}
            />
          </Box>
        )}

        {hasFailedOrders && (
          <Section
            ids={failedIds}
            title={TextConstants.CANNOT_BE_ASSIGNED}
            description={TextConstants.CANNOT_BE_ASSIGNED_DESC}
            icon={warningIcon}
            showAll={showAllFailed}
            onToggleShowAll={toggleShowAllFailed}
            classes={classes}
            isError={true}
          />
        )}
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <Button
          variant="outlined"
          color="primary"
          onClick={handleClose}
          className={classes.actionButton}
        >
          {TextConstants.CANCEL}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={onConfirm}
          className={classes.actionButton}
          disabled={!hasSuccessOrders}
        >
          {TextConstants.CONFIRM_ASSIGNMENT}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CourierSwitchResultDialog;
